import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, Building2 } from 'lucide-react'
import { getDraftFile, putDraftFile } from '@/api/draftFiles'
import { Banner } from '@/components/Banner'
import { ConfirmModal } from '@/components/ConfirmModal'
import { EmptyState } from '@/components/EmptyState'
import { errorMessage } from '@/lib/apiErrors'
import { refreshAfterWrite } from '@/lib/refreshAfterWrite'
import type { ClientsFile, Firm } from '@/types/bundle'
import { ClientsTable, type FirmRow } from './components/ClientsTable'
import { NewOrDuplicateFirmModal } from './components/NewOrDuplicateFirmModal'

function SkeletonBlock() {
  return <div className="h-48 animate-pulse rounded-control bg-surface-raised" />
}

export function ClientsListScreen() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const clientsQuery = useQuery({
    queryKey: ['draft-file', 'clients'],
    queryFn: () => getDraftFile<ClientsFile>('clients'),
  })

  const [duplicateSource, setDuplicateSource] = useState<{ id: string; firm: Firm } | undefined>(undefined)
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<FirmRow | null>(null)

  // Deleting from this list screen is immediate (confirm -> PUT), not staged
  // behind a Save button - same posture as Products'/Documents' own
  // list-screen delete (no other draft/edit state exists on this screen to
  // stage a removal alongside).
  const deleteMutation = useMutation({
    mutationFn: (payload: ClientsFile) => putDraftFile<ClientsFile>('clients', payload),
    onSuccess: () => {
      refreshAfterWrite(queryClient, { kind: 'draft-file', fileKey: 'clients' })
    },
  })

  if (clientsQuery.isPending) {
    return <SkeletonBlock />
  }

  if (clientsQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load clients">
        {errorMessage(clientsQuery.error)}
      </Banner>
    )
  }

  const clientsFile = clientsQuery.data
  const firms = clientsFile.firms
  const firmIds = Object.keys(firms)

  const rows: FirmRow[] = firmIds.map((id) => ({ id, firm: firms[id] }))

  function openNewModal() {
    setDuplicateSource(undefined)
    setModalOpen(true)
  }

  function openDuplicateModal(row: FirmRow) {
    setDuplicateSource({ id: row.id, firm: row.firm })
    setModalOpen(true)
  }

  function handleConfirm(newId: string, initialEntry: Firm) {
    navigate(`/client-overrides/${newId}`, { state: { initialEntry } })
  }

  function handleConfirmDelete() {
    if (!deleteTarget) return
    const nextFirms = { ...firms }
    delete nextFirms[deleteTarget.id]
    deleteMutation.mutate({ ...clientsFile, firms: nextFirms }, { onSuccess: () => setDeleteTarget(null) })
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-title font-semibold text-text-primary">Clients</h1>
        <button
          type="button"
          onClick={openNewModal}
          className="rounded-control bg-accent px-4 py-2 text-body font-medium text-white"
        >
          + New Firm
        </button>
      </div>

      {deleteMutation.isError && (
        <Banner variant="danger" icon={AlertCircle} title="Delete failed">
          {errorMessage(deleteMutation.error)}
        </Banner>
      )}

      {firmIds.length === 0 ? (
        <EmptyState
          icon={Building2}
          headline="No firms defined yet"
          description="Add a firm to give it access and configure its overrides."
          action={{ label: '+ New Firm', onClick: openNewModal }}
        />
      ) : (
        <ClientsTable rows={rows} onDuplicate={openDuplicateModal} onDelete={setDeleteTarget} />
      )}

      <NewOrDuplicateFirmModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        existingIds={firmIds}
        source={duplicateSource?.firm}
        onConfirm={handleConfirm}
      />

      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete firm"
        description={`"${deleteTarget?.firm.name ?? deleteTarget?.id}" will be removed immediately - there is no separate Save step on this screen. This also removes its overrides and its ability to authenticate.`}
        confirmLabel={deleteMutation.isPending ? 'Deleting…' : 'Delete'}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
